/**
 * Pricing helpers for the Virtual Remote subscription products.
 *
 * Both Stripe products use tiered (volume) pricing:
 *   - Product A: recurring every 1 year   -> 12 / 10 / 8 € per unit
 *   - Product B: recurring every 5 years  -> 55 / 45 / 35 € per unit
 *
 * The struck-through reference price of the 5 year product is the equivalent
 * annual tier multiplied by 5, so the discounts are always derived from the
 * live Stripe tiers instead of being hardcoded here.
 */

export const FIVE_YEAR_INTERVAL_COUNT = 5

/**
 * Matches "5 years" in any of the supported languages, so a product whose
 * Stripe price is still configured as a 1 year recurrence is still presented
 * as the 5 year plan (the interval_count is a Stripe-side setting).
 */
const MULTI_YEAR_NAME_REGEX = /(\d+)\s*(a\u00f1os?|anos?|years?|jahre?|ans?|anni|anno)\b/i

function getProductText(product)
{
    return `${product?.name ?? ''} ${product?.description ?? ''}`
}

function getYearsFromText(product)
{
    let match = getProductText(product).match(MULTI_YEAR_NAME_REGEX)

    return (match != null) ? parseInt(match[1], 10) : null
}

export function isFiveYearProduct(product)
{
    return getYearsForProduct(product) > 1
}

export function getYearsForProduct(product)
{
    let recurring = product?.default_price?.recurring

    // An explicit multi-year recurrence always wins
    if (recurring?.interval === 'year' && recurring?.interval_count > 1)
        return recurring.interval_count

    let yearsFromText = getYearsFromText(product)

    if (yearsFromText > 1)
        return yearsFromText

    if (recurring?.interval === 'year' && recurring?.interval_count > 0)
        return recurring.interval_count

    // No recurrence info at all: fall back to the legacy bare "5" check
    return getProductText(product).includes('5') ? FIVE_YEAR_INTERVAL_COUNT : 1
}

/** Normalizes the Stripe tiers into [{ from, to, price }] with prices in euros. */
export function getTiers(product)
{
    let tiers = product?.default_price?.tiers

    if (!Array.isArray(tiers))
        return []

    let from = 1

    return tiers.map(tier =>
    {
        let row =
        {
            from,
            to: (tier.up_to != null) ? tier.up_to : null,
            price: tier.unit_amount / 100
        }

        from = (tier.up_to != null) ? (tier.up_to + 1) : from

        return row
    })
}

export function formatPrice(value)
{
    if (value == null || isNaN(value))
        return null

    let rounded = Math.round(value * 100) / 100

    return Number.isInteger(rounded) ?
            `${rounded}` :
            rounded.toFixed(2).replace(/0$/, '').replace('.', ',')
}

function getSavePercentage(basePrice, price)
{
    if (!basePrice || basePrice <= price)
        return 0

    return Math.floor(((basePrice - price) / basePrice) * 100)
}

/**
 * Builds everything the card needs to render.
 * `annualProduct` is optional and only used to derive the 5 year reference prices.
 */
export function buildPricingModel(product, annualProduct)
{
    let isFiveYear = isFiveYearProduct(product)
    let years = getYearsForProduct(product)
    let tiers = getTiers(product)

    if (tiers.length === 0)
        return null

    let annualTiers = isFiveYear ? getTiers(annualProduct) : tiers

    let getBasePriceFor = (tier, index) =>
    {
        if (isFiveYear)
        {
            // Same bracket in the annual product, times the number of years
            let annualTier = annualTiers.find(t => t.from === tier.from) ?? annualTiers[index]
            return (annualTier != null) ? (annualTier.price * years) : null
        }

        // Annual product: every bracket is compared with the entry price
        return (index === 0) ? null : tiers[0].price
    }

    let rows = tiers.map((tier, index) =>
    {
        let basePrice = getBasePriceFor(tier, index)

        return {
            key: `${tier.from}-${tier.to ?? 'plus'}`,
            quantityLabel: (tier.to != null) ? `${tier.from}-${tier.to}` : `${tier.from}+`,
            price: tier.price,
            basePrice: (basePrice != null && basePrice > tier.price) ? basePrice : null,
            savePercentage: getSavePercentage(basePrice, tier.price),
            perYear: (years > 1) ? (tier.price / years) : null
        }
    })

    let cheapest = Math.min(...tiers.map(t => t.price))
    // Reference for the headline discount: the entry price of the annual plan
    // (times the years) so the 5 year card shows the full combined saving.
    let reference = isFiveYear ?
                    ((annualTiers[0] != null) ? annualTiers[0].price * years : tiers[0].price) :
                    tiers[0].price

    return {
        isFiveYear,
        years,
        rows,
        headlinePrice: tiers[0].price,
        headlinePerYear: (years > 1) ? (tiers[0].price / years) : null,
        maxSavePercentage: getSavePercentage(reference, cheapest)
    }
}
